// A drawing brought up to date (docs/claude/objects.md, *Bringing a drawing up to
// date*): what a file or a paste from another app version holds, as today's
// model reads it. `upgradeDrawing` is a file's whole drawing; `upgradeParts` is
// the ONE per-object step, which a paste runs too, so a pasted part is migrated
// exactly as an opened one. Every id a selection addresses something by is
// minted here (an underlay's, a pane's, an opening's, a partition's), so reading
// a drawing never writes it. DOM-free: what only the app knows comes in through
// `UpgradeEnv`; the view resets of a load stay with main.ts.
import { migrateBenchSkirting } from '../shared/benchPresets';
import type {
  Drawing,
  Lang,
  ProductInstance,
  ProductTemplate,
} from '../shared/types';
import {
  adoptOnLoad,
  settleEnteredName,
} from './catalogueCopy';
import { normalizeCeiling } from './ceiling';
import { drawingDimStyle } from './dimFormat';
import {
  defaultDoor,
  roomDoors,
} from './door';
import { settleElecViewLeaves } from './elecDraw';
import {
  type ElecModel,
  settleElecTexts,
} from './elecSchematic';
import {
  pointInPolygon,
  toInstanceLocal,
} from './geometry';
import {
  linkProductGroups,
  migrateGroupRegistry,
  migrateRelations,
  migrateRoomGroups,
  normalizeGroups,
  settleGroupNames,
} from './groups';
import { migrateHeightFromGround } from './heights';
import {
  type LayerEnv,
  sendSectionBelow,
  settleLayerNames,
} from './layers';
import { migrateA1SetBack } from './lights';
import {
  ensureWallSegments,
  mintFeatureIds,
} from './polyRoom';
import {
  heaterHoles,
  migrateRectRoomToPoly,
  type RoomEnv,
} from './roomRegen';
import { defaultSaunaHeight } from './saunaLayout';
import { settleSheetName } from './sheets';
import { followVertices } from './anchors';

/** What bringing a drawing up to date needs from the app. */
export interface UpgradeEnv {
  /** A part's catalogue product (null: not in the catalogue as loaded). */
  product: (inst: ProductInstance) => ProductTemplate | null;
  /** What stands in a room and the catalogue (the heaters' carve). */
  room: RoomEnv;
  /** layers.ts's: what is a room, the room a part stands in. */
  layers: LayerEnv;
  /** The drawing's language and the dictionary, for names stored as text. */
  lang: Lang;
  trIn: (key: string, lang: Lang) => string;
  /** The electrical schematic's model of the drawing as it now stands. */
  elecModel: () => ElecModel;
  /** A fresh id (ui/core.ts `newId`). */
  newId: (prefix: string) => string;
}

/** A file's drawing with every required field present (the rest kept as saved:
 *  a field-by-field allowlist once dropped whatever it did not list). */
export function drawingWithDefaults(d: Drawing): Drawing {
  return {
    ...d,
    background: d.background ?? null,
    backgroundImage: d.backgroundImage ?? null,
    instances: d.instances ?? [],
    polygons: d.polygons ?? [],
    walls: d.walls ?? [],
    texts: d.texts ?? [],
    dimensions: d.dimensions ?? [],
    connections: d.connections ?? [],
    units: d.units ?? 'mm',
    dimStyle: drawingDimStyle(d),
  };
}

/** Bring a file's drawing up to date, in place: its underlays, its parts
 *  (`upgradeParts`), its groups, its sections, the names it stored as text, and
 *  the heaters' carve of its rooms. */
export function upgradeDrawing(d: Drawing, env: UpgradeEnv): void {
  upgradeBackgrounds(d, env.newId);
  upgradeParts(d, d.instances, env);
  markSnappedDimAnchors(d);
  // A drawing saved before the group registry: its group ids meant four things
  // (a room's contents, a product, related parts, a Ctrl+G group).
  if (!d.groups) {
    migrateRoomGroups(d, (room, part) => !!room.polygon && pointInPolygon(toInstanceLocal(room, part), room.polygon));
    // A saved "related parts" group is a relation now (relId); the parts of a
    // multi-part product in one stay grouped as that product.
    const isProductPart = (i: ProductInstance): boolean => isMultiPartPart(i, env);
    migrateRelations(d, isProductPart);
    migrateGroupRegistry(d);
    // A saved multi-part product's group is linked to its catalogue product.
    linkProductGroups(d, (i) => {
      if (!isProductPart(i)) return null;
      const t = env.product(i);
      return { templateId: i.templateId, partId: t?.parts?.find((p) => p.name === i.name)?.id };
    });
  }
  normalizeGroups(d);
  upgradeSectionsBelow(d, env.layers);
  settleElecTexts(d.elecSchematic, env.lang, env.trIn); // its typed texts kept per language, before names.md
  settleElecViewLeaves(d, env.elecModel); // a view saved listing only what it shows
  for (const sh of d.sheets ?? []) settleSheetName(sh, env.trIn); // "Sheet 3" stored as text
  settleGroupNames(d); // "Group 02" stored as text
  settleLayerNames(d, env.trIn); // "Layer 3" stored as text
  if (!d.enteredNames) {
    for (const inst of d.instances) settleEnteredName(inst, env.product(inst), env.lang); // renamed over its product's names
    d.enteredNames = true;
  }
  // The poly-bench carve footprints (transient, not baked into geometry).
  for (const inst of d.instances) {
    if (inst.autoBenches) { const holes = heaterHoles(inst, env.room); inst.benchHoles = holes.length ? holes : undefined; }
  }
}

/** Bring parts from a file — or a paste, which may come from another window or
 *  app version — up to date, as each is: the ONE per-object step. The parts are
 *  in `d` already, so a dimension anchored to a re-laid room follows it. */
export function upgradeParts(d: Drawing, insts: readonly ProductInstance[], env: Pick<UpgradeEnv, 'product' | 'newId'>): void {
  for (const inst of insts) {
    // Migrate the legacy single `door` into `doors`, and give a walled room saved
    // before rooms had doors (neither field) a default front-wall door — an empty
    // list is a room whose doors were deleted, and it stays without one; backfill
    // the glass height and the height of auto-bench saunas saved before they existed.
    if (inst.autoBenches) {
      const beforeDoors = inst.doors === undefined && inst.door === undefined;
      const ds = roomDoors(inst);
      if (beforeDoors && !ds.length) ds.push(defaultDoor(inst.width, { edge: 0 }, inst.category));
    }
    if (inst.autoBenches && inst.glassHeight == null) inst.glassHeight = 2000;
    if (inst.autoBenches && inst.height == null) inst.height = defaultSaunaHeight(inst.depth);
    // Legacy rectangular rooms become the polygon they always were.
    migrateRectRoomToPoly(inst);
    // Panes / openings saved as spans inside one edge get their own segments.
    followVertices(d, inst.id, () => ensureWallSegments(inst));
    mintFeatureIds(inst, env.newId); // saved before panes, openings and partitions had ids
    normalizeCeiling(inst); // a file from another app version or source
    upgradeRoomSkirting(inst);
    migrateA1SetBack(inst);
    migrateHeightFromGround(inst);
    adoptOnLoad(inst, env.product(inst)); // saved before a copy carried what the views draw it by (a fan's model, …)
  }
}

/** A dimension saved before an anchor said how it was taken: every end on a part
 *  was taken from the part's snap points, so it is re-found among them. */
function markSnappedDimAnchors(d: Drawing): void {
  for (const dim of d.dimensions) for (const ref of [dim.aRef, dim.bRef]) if (ref && !ref.shape && !ref.wall) ref.snapped = true;
}

/** Fold a legacy single `backgroundImage` into the array and give every underlay an id. */
export function upgradeBackgrounds(d: Drawing, newId: (prefix: string) => string): void {
  if (!d.backgroundImages) d.backgroundImages = [];
  if (d.backgroundImage) { d.backgroundImages.push({ ...d.backgroundImage }); d.backgroundImage = null; }
  for (const b of d.backgroundImages) if (!b.id) b.id = newId('bg');
}

/** Every bench of a room (its own, its walls', its partitions') or a sauna bench
 *  saved under an older skirting model — a "closure", a front-board field, a
 *  "+ skirting" preset variant, no boards of its own — read as today's. */
function upgradeRoomSkirting(inst: ProductInstance): void {
  migrateBenchSkirting(inst);
  for (const pb of inst.polyBenches ?? []) migrateBenchSkirting(pb);
  for (const iw of inst.innerWalls ?? []) for (const leg of [...(iw.benches?.a ?? []), ...(iw.benches?.b ?? [])]) migrateBenchSkirting(leg);
}

/** Drawings saved before layers flagged a section `below`: it becomes the bottom
 *  item of the bottom layer, where it paints exactly as it did. */
function upgradeSectionsBelow(d: Drawing, layers: LayerEnv): void {
  // Last first: each goes under the ones before it, so they keep their order.
  for (const sec of [...(d.sections || [])].reverse()) {
    if (!sec.below) continue;
    delete sec.below;
    sendSectionBelow(d, layers, sec.id);
  }
}

/** Whether a part is one part of a placed multi-part product: its catalogue
 *  product has parts, or (its product not loaded) it was placed as one — a
 *  multi-part placement mints `mp-` ids. */
function isMultiPartPart(i: ProductInstance, env: Pick<UpgradeEnv, 'product'>): boolean {
  const t = env.product(i);
  return t ? !!t.parts && t.parts.length > 0 : i.id.startsWith('mp-') || !!i.groupId?.startsWith('mp-');
}
